import { Component, Input, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FOLLOWUP_EMAILS } from '../../data/invitations.seed';
import { addDays, earliestCoachingDate, toScheduleDate } from '../../schedule-validators';

@Component({
  selector: 'app-invitation-step-schedule',
  templateUrl: './step-schedule.component.html',
  styleUrls: ['./step-schedule.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InvitationStepScheduleComponent {
  @Input() form!: FormGroup;

  coachingEmails = FOLLOWUP_EMAILS;
  showCoachingPreview = false;

  /** US date format; the calendar opens on click/focus of the input. */
  bsConfig = { isAnimated: true, dateInputFormat: 'MM/DD/YYYY', showWeekNumbers: false };

  /** Show a field's validation message once it's been touched (or a Next attempt marked it). */
  showError(name: string): boolean {
    const c = this.form.get(name);
    return !!c && c.invalid && c.touched;
  }

  hasError(name: string, error: string): boolean {
    return !!this.form.get(name)?.hasError(error);
  }

  /** Floor each calendar at the date before it, so the order is hard to break rather than
   *  only rejected after the fact. The validators still cover typed input. */
  get minPresentation(): Date | undefined { return toScheduleDate(this.form.get('deadline')?.value) ?? undefined; }

  /** Coaching must clear both rules: strictly after the presentation, and at least tomorrow. */
  get minCoaching(): Date {
    const soonest = earliestCoachingDate();
    const presentation = toScheduleDate(this.form.get('presentation')?.value);
    const dayAfter = presentation ? addDays(presentation, 1) : null;
    return dayAfter && dayAfter.getTime() > soonest.getTime() ? dayAfter : soonest;
  }

  /** Print-friendly window with the coaching emails — the browser's Save as PDF does the rest. */
  downloadCoachingPdf(): void {
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const sections = this.coachingEmails.map(e => `
      <h2>${esc(e.name)}</h2>
      <h3>${esc(e.subject)}</h3>
      <pre>${esc(e.body)}</pre>`).join('');
    const html = `<!doctype html><html><head><title>Personalized coaching emails</title><style>
      body { font-family: 'Open Sans', Arial, sans-serif; color: #3a3f44; padding: 32px; max-width: 720px; margin: 0 auto; }
      h1 { font-size: 20px; } h2 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; color: #1ab394; margin: 24px 0 4px; }
      h3 { font-size: 15px; margin: 0 0 8px; }
      pre { white-space: pre-wrap; font-family: inherit; font-size: 13px; background: #f7f8f9; border: 1px solid #e7eaec; border-radius: 6px; padding: 12px; }
    </style></head><body onload="print()"><h1>Personalized coaching emails</h1>${sections}</body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const w = window.open(url, '_blank', 'noopener');
    if (!w) { URL.revokeObjectURL(url); return; }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
}
